import {
  ShoppingCart,
  CreditCard,
  BarChart,
  Radio,
  Package,
  Globe,
  Zap,
  Wallet,
  Mail,
  Webhook,
  TrendingUp,
  DollarSign,
  Smartphone,
  Eye,
  Target,
  Activity,
  BarChart3,
  Camera,
  Shield,
  Bitcoin,
  Stethoscope,
} from 'lucide-react';
import { brandingConfig, type BrandConfig } from '@/config/branding';

// ============================================================================
// Types
// ============================================================================

export type IntegrationType =
  | 'storefront'
  | 'payment'
  | 'ads_tracking'
  | 'pixel'
  | 'analytics'
  | 'automation'
  | 'checkout_pickup_point'
  | 'charge_back'
  | 'tax'
  | 'post_purchase_hook'
  | 'telehealth';

export type IntegrationProvider =
  | 'shopify'
  | 'woocommerce'
  | 'apple_pay'
  | 'google_pay'
  | 'paypal'
  | 'klarna'
  | 'bridge'
  | 'whop'
  | 'convesiopay'
  | 'hipay'
  | 'tabby'
  | 'oceanpayment'
  | 'facebook'
  | 'tiktok'
  | 'meta_conversion'
  | 'awin_conversion'
  | 'gtm'
  | 'clarity'
  | 'snapchat'
  | 'klaviyo'
  | 'converge'
  | 'triplewhale'
  | 'webhooks'
  | 'zapier'
  | 'sendcloud'
  | 'chargeblast'
  | 'airwallex_apm'
  | 'crypto'
  | 'crypto_onchain'
  | 'zelle'
  | 'custom_payment'
  | 'taxjar'
  | 'taxcloud'
  | 'wetracked'
  | 'clickflare'
  | 'voluum'
  | 'binom'
  | 'redtrack'
  | 'clickmagick'
  | 'hyros'
  | 'platformpay'
  | 'tagada_rx'
  | 'whop_platforms'
  | 'slash';

export interface IntegrationMetadata {
  id: IntegrationProvider;
  name: string;
  description: string;
  category: IntegrationType;
  icon: React.ReactNode;
  color: string; // Tailwind classes
  tags: string[];
  logoUrl?: string;
  documentationUrl?: string;
  /** External URL for integrations that link out (e.g., Zapier invite link) */
  externalUrl?: string;
  /** If true, only show for TagadaPay (not whitelabel) */
  tagadaOnly?: boolean;
  /**
   * Tagada Rx white-label gate: shown iff the server says the account may
   * use Rx (direct Tagada merchant, or partner whose active signed CRM
   * buy-rate version has the TagadaRx line) or Rx is already requested.
   * See `rx.integrationStatus.availableForAccount`.
   */
  rxGated?: boolean;
  /** False while the integration is merged but not open to merchants: the list leaves it out. */
  isAvailable?: boolean;
}

/**
 * Slash Payments is merged but not available yet (#TAG-2955): nothing has run against Slash's
 * live API. Same switch as `IS_SLASH_AVAILABLE` in the backend; both flip in the live-test MR.
 */
export const IS_SLASH_AVAILABLE = false;

// ============================================================================
// Categories
// ============================================================================

export const INTEGRATION_CATEGORIES = [
  { id: 'all', name: 'All', icon: '🔌' },
  { id: 'storefront', name: 'E-commerce', icon: '🏪' },
  { id: 'payment', name: 'Payment Methods', icon: '💳' },
  { id: 'pixel', name: 'Tracking Pixels', icon: '📊' },
  { id: 'analytics', name: 'Analytics', icon: '📈' },
  { id: 'automation', name: 'Automation', icon: '⚡' },
  { id: 'checkout_pickup_point', name: 'Pickup Points', icon: '📦' },
  { id: 'charge_back', name: 'Chargeback', icon: '💰' },
  { id: 'tax', name: 'Tax', icon: '🧾' },
  { id: 'post_purchase_hook', name: 'Post-Purchase', icon: '🔁' },
  { id: 'telehealth', name: 'Telehealth / Rx', icon: '🩺' },
] as const;

// ============================================================================
// Integrations Metadata
// ============================================================================

export const buildIntegrationsMetadata = (brand: BrandConfig): IntegrationMetadata[] => [
  // ============================================================================
  // E-commerce / Storefront (2)
  // ============================================================================
  {
    id: 'shopify',
    name: 'Shopify',
    description: 'Connect your Shopify store to sync products and orders',
    category: 'storefront',
    icon: <ShoppingCart className="h-6 w-6" />,
    color: 'bg-green-500/10 text-green-600',
    tags: ['E-commerce', 'Products', 'Orders', 'Sync', 'Inventory'],
    logoUrl: '/integrations/shopify-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/shopify`,
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    description: 'Connect your WooCommerce store to sync products and orders',
    category: 'storefront',
    icon: <ShoppingCart className="h-6 w-6" />,
    color: 'bg-purple-500/10 text-purple-600',
    tags: ['WordPress', 'E-commerce', 'Products', 'Orders'],
    logoUrl: '/integrations/woo-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/woocommerce`,
  },

  // ============================================================================
  // Payment Methods (8)
  // ============================================================================
  {
    id: 'paypal',
    name: 'PayPal',
    description: 'Accept PayPal payments',
    category: 'payment',
    icon: <CreditCard className="h-6 w-6" />,
    color: 'bg-blue-500/10 text-blue-600',
    tags: ['Payment Gateway', 'Wallet', 'Checkout', 'Credit Card'],
    logoUrl: '/integrations/paypal.svg',
    documentationUrl: `${brand.docsUrl}/integrations/paypal`,
  },
  {
    id: 'apple_pay',
    name: 'Apple Pay',
    description: 'Accept Apple Pay payments',
    category: 'payment',
    icon: <Smartphone className="h-6 w-6" />,
    color: 'bg-gray-500/10 text-gray-900',
    tags: ['Mobile Payment', 'Wallet', 'iOS', 'One-tap'],
    logoUrl: '/integrations/apple-pay.svg',
    documentationUrl: `${brand.docsUrl}/integrations/apple-pay`,
  },
  {
    id: 'google_pay',
    name: 'Google Pay',
    description: 'Accept Google Pay payments',
    category: 'payment',
    icon: <Wallet className="h-6 w-6" />,
    color: 'bg-red-500/10 text-red-600',
    tags: ['Mobile Payment', 'Wallet', 'Android', 'One-tap'],
    logoUrl: '/integrations/google-pay.svg',
    documentationUrl: `${brand.docsUrl}/integrations/google-pay`,
  },
  {
    id: 'klarna',
    name: 'Klarna',
    description: 'Offer buy now, pay later payment options',
    category: 'payment',
    icon: <DollarSign className="h-6 w-6" />,
    color: 'bg-pink-500/10 text-pink-600',
    tags: ['BNPL', 'Financing', 'Installments', 'Buy Now Pay Later'],
    logoUrl: '/integrations/klarna.svg',
    documentationUrl: `${brand.docsUrl}/integrations/klarna`,
  },
  {
    id: 'bridge',
    name: 'Bridge',
    description: 'Accept bank transfers and open banking payments',
    category: 'payment',
    icon: <CreditCard className="h-6 w-6" />,
    color: 'bg-indigo-500/10 text-indigo-600',
    tags: ['Bank Transfer', 'Open Banking', 'Europe', 'Instant Payment'],
    logoUrl: '/integrations/bridge-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/bridge`,
  },
  {
    id: 'whop',
    name: 'Whop',
    description: 'Accept payments for digital products and memberships',
    category: 'payment',
    icon: <Zap className="h-6 w-6" />,
    color: 'bg-violet-500/10 text-violet-600',
    tags: ['Digital Products', 'Subscriptions', 'Memberships', 'Courses'],
    logoUrl: '/integrations/whop.svg',
    documentationUrl: `${brand.docsUrl}/integrations/whop`,
  },
  {
    id: 'whop_platforms',
    name: 'Whop V2 (Beta)', // let the users know it's still in beta
    description: 'Connect your Whop company as a payment processor — accept one-time and recurring payments via your Whop connected account',
    category: 'payment',
    icon: <Zap className="h-6 w-6" />,
    color: 'bg-violet-500/10 text-violet-600',
    tags: ['Whop', 'Connected Account', 'Platform', 'Digital Products'],
    logoUrl: '/integrations/whop.svg',
  },
  {
    id: 'slash',
    name: 'Slash (Beta)',
    isAvailable: IS_SLASH_AVAILABLE,
    description: 'Accept card payments with Slash Payments, on your own Slash account',
    category: 'payment',
    icon: <Wallet className="h-6 w-6" />,
    color: 'bg-zinc-500/10 text-zinc-700',
    tags: ['Payment Gateway', 'Card Processing', 'USD', 'EUR'],
    documentationUrl: 'https://docs.slash.com/docs/payments/quickstart',
  },
  {
    id: 'convesiopay',
    name: 'ConvesioPay',
    description: 'Accept payments with ConvesioPay payment gateway',
    category: 'payment',
    icon: <Globe className="h-6 w-6" />,
    color: 'bg-teal-500/10 text-teal-600',
    tags: ['Payment Gateway', 'Multi-currency', 'Global', 'Card Processing'],
    logoUrl: '/integrations/convesiopay.svg',
    documentationUrl: `${brand.docsUrl}/integrations/convesiopay`,
  },
  {
    id: 'hipay',
    name: 'HiPay',
    description: 'Accept payments with HiPay Enterprise payment gateway',
    category: 'payment',
    icon: <Globe className="h-6 w-6" />,
    color: 'bg-orange-500/10 text-orange-600',
    tags: ['Payment Gateway', 'Europe', 'Card Processing', 'Multi-currency', '3DS'],
    logoUrl: '/integrations/hipay.svg',
    documentationUrl: 'https://developer.hipay.com/online-payments/sdk-reference/sdk-js',
  },
  {
    id: 'tabby',
    name: 'Tabby',
    description: 'Accept buy now, pay later payments with Tabby (UAE, KSA, Kuwait)',
    category: 'payment',
    icon: <Globe className="h-6 w-6" />,
    color: 'bg-green-500/10 text-green-600',
    tags: ['BNPL', 'Buy Now Pay Later', 'Middle East', 'Installments'],
    logoUrl: '/integrations/tabby.svg',
    documentationUrl: 'https://docs.tabby.ai',
  },
  {
    id: 'oceanpayment',
    name: 'OceanPayment',
    description: 'Accept payments with OceanPayment gateway',
    category: 'payment',
    icon: <Globe className="h-6 w-6" />,
    color: 'bg-cyan-500/10 text-cyan-600',
    tags: ['Payment Gateway', 'Cross-border', 'International', 'Multi-currency'],
    logoUrl: '/integrations/oceanpayment.svg',
    documentationUrl: `${brand.docsUrl}/integrations/oceanpayment`,
  },
  {
    id: 'airwallex_apm',
    name: 'Airwallex Payment Methods',
    description: 'Enable alternative payment methods (Klarna, Afterpay, PayPal) via Airwallex',
    category: 'payment',
    icon: <Wallet className="h-6 w-6" />,
    color: 'bg-purple-500/10 text-purple-600',
    tags: ['Airwallex', 'Klarna', 'Afterpay', 'PayPal', 'APM', 'Alternative Payments'],
    logoUrl: '/integrations/airwallex-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/airwallex-apm`,
  },

  // Card to crypto: the buyer pays by card at an on-ramp (Rampex), which runs the buyer's KYC; the
  // merchant is paid in USDC. `name` and `description` are saved on the integration row, and the
  // funnel step's payment panel offers them as the shopper's default label: the merchant-facing copy
  // lives in the `integrations` locale (`names.crypto`, `descriptions.crypto`).
  {
    id: 'crypto',
    name: 'Crypto',
    description: 'Pay by card',
    category: 'payment',
    icon: <Bitcoin className="h-6 w-6" />,
    color: 'bg-orange-500/10 text-orange-600',
    tags: ['Card', 'USDC payout', 'Buyer KYC', 'Crypto', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay'],
    logoUrl: brand.defaultIntegrationLogo ?? brand.logo,
    documentationUrl: `${brand.docsUrl}/integrations/crypto`,
  },
  // On-chain crypto (customer pays in crypto, settled to the merchant's wallets by the platform).
  // Distinct from `crypto`, the Rampex fiat-to-crypto on-ramp above.
  {
    id: 'crypto_onchain',
    name: 'Crypto payments',
    description: 'Accept crypto paid on-chain; keys and settlement are managed by the platform',
    category: 'payment',
    icon: <Bitcoin className="h-6 w-6" />,
    color: 'bg-amber-500/10 text-amber-600',
    tags: ['Crypto', 'Crypto payout', 'USDC', 'USDT', 'ETH', 'SOL'],
    logoUrl: brand.defaultIntegrationLogo ?? brand.logo,
    documentationUrl: `${brand.docsUrl}/integrations/crypto-payments`,
    tagadaOnly: true,
  },
  {
    id: 'zelle',
    name: 'Zelle',
    description: 'Accept payments via Zelle — customers pay to your email after placing the order',
    category: 'payment',
    icon: <Wallet className="h-6 w-6" />,
    color: 'bg-purple-500/10 text-purple-600',
    tags: ['Bank Transfer', 'P2P', 'US', 'On Hold', 'Manual'],
    logoUrl: '/integrations/zelle.svg',
  },
  {
    id: 'custom_payment',
    name: 'Custom payment',
    description: 'Accept manual payments (Venmo, wire transfer, COD…) with your own name, logo, and instructions',
    category: 'payment',
    icon: <DollarSign className="h-6 w-6" />,
    color: 'bg-slate-500/10 text-slate-700',
    tags: ['Manual', 'Instructions', 'On Hold', 'Custom'],
  },

  // ============================================================================
  // Tracking Pixels (7)
  // ============================================================================
  {
    id: 'facebook',
    name: 'Facebook Pixel',
    description: 'Track conversions from Facebook ads',
    category: 'pixel',
    icon: <Radio className="h-6 w-6" />,
    color: 'bg-blue-600/10 text-blue-700',
    tags: ['Advertising', 'Tracking', 'Conversions', 'Meta', 'Social Media'],
    logoUrl: '/integrations/fb-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/facebook`,
  },
  {
    id: 'tiktok',
    name: 'TikTok Pixel',
    description: 'Track conversions from TikTok ads',
    category: 'pixel',
    icon: <Camera className="h-6 w-6" />,
    color: 'bg-black/10 text-black',
    tags: ['Advertising', 'Social Media', 'Tracking', 'Conversions', 'Video'],
    logoUrl: '/integrations/tiktok-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/tiktok`,
  },
  {
    id: 'meta_conversion',
    name: 'Meta Conversion API',
    description: 'Server-side conversion tracking for Facebook and Instagram',
    category: 'pixel',
    icon: <Target className="h-6 w-6" />,
    color: 'bg-blue-500/10 text-blue-600',
    tags: ['Server-side', 'Meta', 'Facebook', 'Instagram', 'Conversion API'],
    logoUrl: '/integrations/fb-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/meta-conversion`,
  },
  {
    id: 'awin_conversion',
    name: 'AWIN Conversion',
    description: 'Track affiliate conversions with AWIN',
    category: 'pixel',
    icon: <TrendingUp className="h-6 w-6" />,
    color: 'bg-orange-500/10 text-orange-600',
    tags: ['Affiliate', 'Conversions', 'Marketing', 'Tracking', 'Network'],
    logoUrl: '/integrations/awin.svg',
    documentationUrl: `${brand.docsUrl}/integrations/awin`,
  },
  {
    id: 'gtm',
    name: 'Google Tag Manager',
    description: 'Manage tracking tags with Google Tag Manager',
    category: 'pixel',
    icon: <Activity className="h-6 w-6" />,
    color: 'bg-yellow-500/10 text-yellow-700',
    tags: ['Tag Management', 'Google', 'Analytics', 'Tracking', 'Automation'],
    logoUrl: '/integrations/gtm.svg',
    documentationUrl: `${brand.docsUrl}/integrations/gtm`,
  },
  {
    id: 'clarity',
    name: 'Microsoft Clarity',
    description: 'Track user behavior with session recordings and heatmaps',
    category: 'pixel',
    icon: <Eye className="h-6 w-6" />,
    color: 'bg-blue-400/10 text-blue-600',
    tags: ['Heatmaps', 'Session Recording', 'Analytics', 'User Behavior', 'Microsoft'],
    logoUrl: '/integrations/clarity.svg',
    documentationUrl: `${brand.docsUrl}/integrations/clarity`,
  },
  {
    id: 'snapchat',
    name: 'Snapchat Pixel',
    description: 'Track conversions from Snapchat ads',
    category: 'pixel',
    icon: <Camera className="h-6 w-6" />,
    color: 'bg-yellow-400/10 text-yellow-600',
    tags: ['Advertising', 'Social Media', 'Tracking', 'Conversions', 'Snapchat'],
    logoUrl: '/integrations/snapchat.svg',
    documentationUrl: `${brand.docsUrl}/integrations/snapchat`,
  },
  {
    id: 'wetracked',
    name: 'Wetracked',
    description: 'E-commerce attribution pixel for Shopify stores (auto-injected via theme)',
    category: 'pixel',
    icon: <Activity className="h-6 w-6" />,
    color: 'bg-emerald-500/10 text-emerald-600',
    tags: ['Attribution', 'Shopify', 'Conversions', 'Tracking', 'Pixel'],
    logoUrl: '/integrations/wetracked.png',
    documentationUrl: `${brand.docsUrl}/integrations/wetracked`,
  },
  {
    id: 'clickflare',
    name: 'ClickFlare',
    description: 'Server-side conversion postback for ClickFlare. Auto-fires on every paid order using the click_id captured on the lander.',
    category: 'pixel',
    icon: <Target className="h-6 w-6" />,
    color: 'bg-orange-500/10 text-orange-600',
    tags: ['Server-side', 'Postback', 'Attribution', 'Ad Tracker'],
    logoUrl: '/integrations/clickflare.png',
    documentationUrl: `${brand.docsUrl}/integrations/clickflare`,
  },
  {
    id: 'voluum',
    name: 'Voluum',
    description: 'Server-side conversion postback for Voluum. Auto-fires on every paid order using the click_id captured on the lander.',
    category: 'pixel',
    icon: <Target className="h-6 w-6" />,
    color: 'bg-rose-500/10 text-rose-600',
    tags: ['Server-side', 'Postback', 'Attribution', 'Ad Tracker'],
    logoUrl: '/integrations/voluum.png',
    documentationUrl: `${brand.docsUrl}/integrations/voluum`,
  },
  {
    id: 'binom',
    name: 'Binom',
    description: 'Server-side conversion postback for Binom. Auto-fires on every paid order using the click_id captured on the lander.',
    category: 'pixel',
    icon: <Target className="h-6 w-6" />,
    color: 'bg-indigo-500/10 text-indigo-700',
    tags: ['Server-side', 'Postback', 'Attribution', 'Ad Tracker'],
    logoUrl: '/integrations/binom.png',
    documentationUrl: `${brand.docsUrl}/integrations/binom`,
  },
  {
    id: 'redtrack',
    name: 'RedTrack',
    description: 'Server-side conversion postback for RedTrack. Auto-fires on every paid order using the click_id captured on the lander.',
    category: 'pixel',
    icon: <Target className="h-6 w-6" />,
    color: 'bg-red-500/10 text-red-600',
    tags: ['Server-side', 'Postback', 'Attribution', 'Ad Tracker'],
    logoUrl: '/integrations/redtrack.png',
    documentationUrl: `${brand.docsUrl}/integrations/redtrack`,
  },
  {
    id: 'clickmagick',
    name: 'ClickMagick',
    description: 'Server-side conversion postback for ClickMagick. Auto-fires on every paid order using the click_id captured on the lander.',
    category: 'pixel',
    icon: <Target className="h-6 w-6" />,
    color: 'bg-fuchsia-500/10 text-fuchsia-600',
    tags: ['Server-side', 'Postback', 'Attribution', 'Ad Tracker'],
    logoUrl: '/integrations/clickmagick.png',
    documentationUrl: `${brand.docsUrl}/integrations/clickmagick`,
  },
  {
    id: 'hyros',
    name: 'Hyros',
    description: 'Email + phone attribution. Auto-pushes paid orders to Hyros so it can credit the original ad source — works across iOS, adblockers, and devices.',
    category: 'pixel',
    icon: <BarChart3 className="h-6 w-6" />,
    color: 'bg-cyan-500/10 text-cyan-700',
    tags: ['Server-side', 'Attribution', 'Email-based', 'Ad Tracker', 'Cross-device'],
    logoUrl: '/integrations/hyros.png',
    documentationUrl: `${brand.docsUrl}/integrations/hyros`,
  },

  // ============================================================================
  // Analytics (4)
  // ============================================================================
  {
    id: 'klaviyo',
    name: 'Klaviyo',
    description: 'Send customer data to Klaviyo for email and SMS marketing',
    category: 'analytics',
    icon: <Mail className="h-6 w-6" />,
    color: 'bg-green-600/10 text-green-700',
    tags: ['Email Marketing', 'SMS', 'Automation', 'Segmentation', 'Campaigns'],
    logoUrl: '/integrations/klaviyo.svg',
    documentationUrl: `${brand.docsUrl}/integrations/klaviyo`,
  },
  {
    id: 'converge',
    name: 'Converge',
    description: 'Send conversion data to Converge for marketing attribution',
    category: 'analytics',
    icon: <BarChart3 className="h-6 w-6" />,
    color: 'bg-purple-600/10 text-purple-700',
    tags: ['Attribution', 'Tracking', 'Analytics', 'Marketing', 'Integration'],
    logoUrl: '/integrations/converge.jpg',
    documentationUrl: `${brand.docsUrl}/integrations/converge`,
  },
  {
    id: 'triplewhale',
    name: 'Triple Whale',
    description: 'Send order data to Triple Whale for analytics',
    category: 'analytics',
    icon: <BarChart className="h-6 w-6" />,
    color: 'bg-indigo-600/10 text-indigo-700',
    tags: ['E-commerce Analytics', 'Attribution', 'Metrics', 'ROI', 'Dashboard'],
    logoUrl: '/integrations/triplewhale.png',
    documentationUrl: `${brand.docsUrl}/integrations/triplewhale`,
  },
  {
    id: 'webhooks',
    name: 'Webhooks',
    description: 'Send real-time event notifications to custom endpoints',
    category: 'analytics',
    icon: <Webhook className="h-6 w-6" />,
    color: 'bg-gray-600/10 text-gray-700',
    tags: ['Developer', 'API', 'Events', 'Real-time', 'Automation', 'Custom'],
    logoUrl: '/integrations/webhook-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/webhooks`,
  },

  // ============================================================================
  // Automation (1)
  // ============================================================================
  {
    id: 'zapier',
    name: 'Zapier',
    description: `Connect ${brand.appName} to 7,000+ apps and automate your workflows`,
    category: 'automation',
    icon: <Zap className="h-6 w-6" />,
    color: 'bg-orange-500/10 text-orange-600',
    tags: ['Automation', 'Workflows', 'No-code', 'Integration', 'Zapier'],
    logoUrl: '/integrations/zapier-icon.svg',
    documentationUrl: `${brand.docsUrl}/integrations/zapier`,
    externalUrl: 'https://zapier.com/developer/public-invite/234798/58725d49c3ef6c6e3c03650fca157c2b/',
    tagadaOnly: true,
  },

  // ============================================================================
  // Shipping / Pickup Points (1)
  // ============================================================================
  {
    id: 'sendcloud',
    name: 'SendCloud',
    description: 'Offer pickup point selection at checkout',
    category: 'checkout_pickup_point',
    icon: <Package className="h-6 w-6" />,
    color: 'bg-emerald-500/10 text-emerald-600',
    tags: ['Shipping', 'Pickup Points', 'Multi-carrier', 'Delivery', 'Logistics'],
    logoUrl: '/integrations/sendcloud-icon.png',
    documentationUrl: `${brand.docsUrl}/integrations/sendcloud`,
  },

  // ============================================================================
  // Chargeback Protection (1)
  // ============================================================================
  {
    id: 'chargeblast',
    name: 'Chargeblast',
    description: 'Automated chargeback prevention and management',
    category: 'charge_back',
    icon: <Shield className="h-6 w-6" />,
    color: 'bg-red-600/10 text-red-700',
    tags: ['Chargeback', 'Fraud Prevention', 'Disputes', 'Protection', 'Risk'],
    logoUrl: '/integrations/chargeblast.png',
    documentationUrl: `${brand.docsUrl}/integrations/chargeblast`,
  },

  // ============================================================================
  // Tax (2)
  // ============================================================================
  {
    id: 'taxjar',
    name: 'TaxJar',
    description: 'Automate sales tax calculations, reporting, and filing',
    category: 'tax',
    icon: <DollarSign className="h-6 w-6" />,
    color: 'bg-green-500/10 text-green-600',
    tags: ['Sales Tax', 'Tax Compliance', 'Tax Filing', 'Automation'],
    logoUrl: '/integrations/taxjar.png',
    documentationUrl: 'https://developers.taxjar.com/api/reference/',
  },
  {
    id: 'taxcloud',
    name: 'TaxCloud',
    description: 'Calculate, report, and file US sales tax across every jurisdiction',
    category: 'tax',
    icon: <DollarSign className="h-6 w-6" />,
    color: 'bg-green-500/10 text-green-600',
    tags: ['Sales Tax', 'Tax Compliance', 'Tax Filing', 'Automation'],
    logoUrl: '/integrations/taxcloud.png',
    documentationUrl: 'https://docs.taxcloud.com/',
  },

  // ============================================================================
  // Post-Purchase Hooks (1)
  // ============================================================================
  {
    id: 'platformpay',
    name: 'PlatformPay',
    description: 'Forward completed orders to the PlatformPay.io live data feed',
    category: 'post_purchase_hook',
    icon: <Activity className="h-6 w-6" />,
    color: 'bg-blue-500/10 text-blue-600',
    tags: ['Reporting', 'Post-Purchase', 'Live Feed', 'Optimization'],
    documentationUrl: 'https://platformpay.io',
  },

  // ============================================================================
  // Telehealth / Rx (1)
  // ============================================================================
  {
    id: 'tagada_rx',
    name: 'Tagada Rx',
    description:
      'Sell prescription treatments with licensed clinician review, pharmacy fulfillment, and rebill on ship day. Apply here — Tagada reviews and unlocks your workspace.',
    category: 'telehealth',
    icon: <Stethoscope className="h-6 w-6" />,
    color: 'bg-emerald-500/10 text-emerald-700',
    tags: ['Rx', 'Telehealth', 'Prescription'],
    logoUrl: brand.defaultIntegrationLogo ?? brand.logo,
    documentationUrl: `${brand.docsUrl}/developer-tools/rx/introduction`,
    rxGated: true,
  },
];

export const INTEGRATIONS_METADATA = buildIntegrationsMetadata(brandingConfig);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get integration metadata by provider ID
 */
export function getIntegrationMetadata(
  provider: IntegrationProvider,
  metadataList: IntegrationMetadata[] = INTEGRATIONS_METADATA,
): IntegrationMetadata | undefined {
  return metadataList.find((integration) => integration.id === provider);
}

/**
 * Get all integrations by category
 */
export function getIntegrationsByCategory(category: IntegrationType): IntegrationMetadata[] {
  return INTEGRATIONS_METADATA.filter((integration) => integration.category === category);
}

/**
 * Search integrations by name or tags
 */
export function searchIntegrations(query: string): IntegrationMetadata[] {
  const normalizedQuery = query.toLowerCase().trim();

  if (!normalizedQuery) {
    return INTEGRATIONS_METADATA;
  }

  return INTEGRATIONS_METADATA.filter(
    (integration) =>
      integration.name.toLowerCase().includes(normalizedQuery) ||
      integration.description.toLowerCase().includes(normalizedQuery) ||
      integration.tags.some((tag) => tag.toLowerCase().includes(normalizedQuery)),
  );
}

/**
 * Get integration count by category
 */
export function getIntegrationCountByCategory(): Record<string, number> {
  const counts: Record<string, number> = {
    all: INTEGRATIONS_METADATA.length,
  };

  INTEGRATIONS_METADATA.forEach((integration) => {
    counts[integration.category] = (counts[integration.category] || 0) + 1;
  });

  return counts;
}
